import React, { FC } from "react";
import { ReactSVG } from "react-svg";

/**
 * Icons for the message-type components.
 *
 * The app already ships its own icon set in public/images and renders it the
 * same way everywhere: SVGs through <ReactSVG> (so they can be recoloured with
 * CSS) and PNGs through a plain <img> — see SettingsPanel, MapPanel and
 * MessageItem. These components were reaching for @ant-design/icons instead,
 * which is why they read as a different product.
 *
 * Only names listed here exist as real assets. Anything genuinely missing from
 * the set (search, sort, mail) stays on antd rather than inventing artwork.
 */

/* SVG assets — recolourable, sized via beforeInjection. */
const SVG_ICONS = {
  calendar: "/images/calendar-icon.svg",
  clock: "/images/clock-icon.svg",
  timestamp: "/images/timestamp-icon.svg",
  location: "/images/location-icon.svg",
  geolocation: "/images/geolocation-icon.svg",
  gps: "/images/gps-icon.svg",
  route: "/images/map-line-icon.svg",
  area: "/images/area-icon.svg",
  filter: "/images/filter-icon.svg",
  doc: "/images/doc-icon.svg",
  exportCsv: "/images/export-csv.svg",
  exportPdf: "/images/export-pdf.svg",
  exportXls: "/images/export-xls.svg",
  message: "/images/message-icon.svg",
  call: "/images/call-icon.svg",
  user: "/images/user-icon.svg",
  community: "/images/community-icon.svg",
  communityBlue: "/images/community-blue-icon.svg",
  heart: "/images/heart-icon.svg",
  car: "/images/car-icon.svg",
  camera: "/images/camera-icon.svg",
  emergency: "/images/emergency-icon.svg",
  broadcast: "/images/broadcast-icon.svg",
  refresh: "/images/refresh-icon.svg",
  expand: "/images/expand-icon.svg",
  close: "/images/close-icon.svg",
  next: "/images/next-icon.svg",
  plus: "/images/plus-icon.svg",
  edit: "/images/edit-icon.svg",
  payment: "/images/payment-icon.svg",
  media: "/images/media-icon.svg",
  send: "/images/send.svg",
  flag: "/images/flag-icon.svg",
  bell: "/images/notification-icon.svg",
  markerBlue: "/images/map-plain-icon-blue.svg",
  markerGreen: "/images/map-plain-icon-green.svg",
  markerOrange: "/images/map-plain-icon-orange.svg",
  markerGrey: "/images/map-plain-icon-grey.svg",
} as const;

/* Raster assets — fixed artwork, cannot be recoloured. */
const PNG_ICONS = {
  alert: "/images/alert-icon.png",
  alertConfig: "/images/alert-configuration-icon.png",
  warning: "/images/warning.png",
  survey: "/images/Survey-icon.png",
  summary: "/images/summary-icon.png",
  rsvp: "/images/rsvp-icon.png",
  people: "/images/people-icon.png",
  pay: "/images/Pay-Yellow-icon.png",
  event: "/images/event-icon.png",
  gpsReport: "/images/gps-report-icon.png",
  gpsLive: "/images/GPS Live-icon.png",
  image: "/images/Image-icon.png",
  audio: "/images/Audio-icon.png",
  video: "/images/video-recording-icon.png",
  capture: "/images/capture-icon.png",
  place: "/images/place-icon.png",
  file: "/images/File-icon.png",
} as const;

export type BeamIconName = keyof typeof SVG_ICONS | keyof typeof PNG_ICONS;

interface BeamIconProps {
  name: BeamIconName;
  size?: number;
  className?: string;
  title?: string;
}

const BeamIcon: FC<BeamIconProps> = ({ name, size = 16, className = "", title }) => {
  const pngSrc = (PNG_ICONS as Record<string, string>)[name];

  if (pngSrc) {
    return (
      <img
        className={`beam-icon ${className}`}
        src={pngSrc}
        alt={title || ""}
        title={title}
        width={size}
        height={size}
      />
    );
  }

  return (
    <ReactSVG
      className={`beam-icon ${className}`}
      src={(SVG_ICONS as Record<string, string>)[name]}
      title={title}
      beforeInjection={(svg) => {
        svg.setAttribute("width", String(size));
        svg.setAttribute("height", String(size));
      }}
    />
  );
};

/**
 * public/images/files-icon holds artwork per file extension. Anything without
 * its own file (csv, txt, …) falls back to the generic sheet.
 */
const FILE_ICON_EXTS = [
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "rar", "exe", "document",
];

export const fileIconSrc = (nameOrExt: string): string => {
  const ext = (nameOrExt.split(".").pop() || "").toLowerCase();
  return FILE_ICON_EXTS.indexOf(ext) !== -1
    ? `/images/files-icon/${ext}.png`
    : "/images/files-icon/file.png";
};

/** Matches MessageItem's `/images/{messageType}-icon.png` convention. */
export const messageTypeIconSrc = (messageType: string): string =>
  `/images/${messageType}-icon.png`;

export default BeamIcon;
